import { ITokenModel } from "../../models";

type RefreshFn = () => Promise<ITokenModel>;

type SessionManagerEventMap = {
  "refresh-success": ITokenModel;
  "refresh-failure": unknown;
};

type EventListener<T> = (payload: T) => void | Promise<void>;

type ListenerMap = {
  [K in keyof SessionManagerEventMap]?: Set<
    EventListener<SessionManagerEventMap[K]>
  >;
};

class SessionManager {
  private isRefreshing = false;
  private isLoggingOut = false;

  private queue: Array<{
    resolve: (token: ITokenModel) => void;
    reject: (err: unknown) => void;
  }> = [];

  private refreshFn?: RefreshFn;
  private listeners: ListenerMap = {};

  setRefreshFn(fn: RefreshFn): void {
    this.refreshFn = fn;
  }

  private getOrCreateListeners<K extends keyof SessionManagerEventMap>(
    event: K
  ): Set<EventListener<SessionManagerEventMap[K]>> {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set() as unknown as ListenerMap[K];
    }

    return this.listeners[event] as Set<
      EventListener<SessionManagerEventMap[K]>
    >;
  }

  on<K extends keyof SessionManagerEventMap>(
    event: K,
    listener: EventListener<SessionManagerEventMap[K]>
  ): () => void {
    const listeners = this.getOrCreateListeners(event);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  private async emit<K extends keyof SessionManagerEventMap>(
    event: K,
    payload: SessionManagerEventMap[K]
  ): Promise<void> {
    const listeners = this.getOrCreateListeners(event);

    await Promise.all(
      Array.from(listeners).map((listener) =>
        Promise.resolve(listener(payload))
      )
    );
  }

  async refresh(): Promise<ITokenModel> {
    if (!this.refreshFn) {
      throw new Error("Refresh function not set");
    }

    if (this.isRefreshing) {
      return new Promise<ITokenModel>((resolve, reject) => {
        this.queue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const session = await this.refreshFn();

      this.queue.forEach((p) => p.resolve(session));
      this.queue = [];

      this.handleRefreshSuccess(session);

      return session;
    } catch (error) {
      this.queue.forEach((p) => p.reject(error));
      this.queue = [];

      this.handleRefreshFailure(error);

      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  private async handleRefreshSuccess(session: ITokenModel): Promise<void> {
    await this.emit("refresh-success", session);
  }

  private async handleRefreshFailure(error: unknown): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    try {
      await this.emit("refresh-failure", error);
    } finally {
      this.isLoggingOut = false;
    }
  }
}

export const sessionManager = new SessionManager();
